import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';

import { Box, Panel, Button, Text, Layout, Stack } from 'src/components/matchbox';
import Metric from './Forms/Metric';
import AlertSummary from './AlertSummary';
import Footer from './Footer';
import Data from './Forms/Data';
import Condition from './Forms/Condition';
import Details from './Forms/Details';
import Notifications from './Forms/Notifications';
import { tokens } from '@sparkpost/design-tokens';
import { useAlertForm } from './useAlertForm';
import { Form } from 'src/components/tracking';
import { Progress } from 'src/components/matchbox';
import { useFillAlertForm } from './useFillAlertForm';

const TAB_METRIC_AND_FILTERING = 1;
const TAB_DATA_MEASUREMENT = 2;
const TAB_CONDITION_CONFIGURATION = 3;
const TAB_DETAILS_AND_NOTIFICATIONS = 4;

const tabsLabels = [
  'Metric and Filtering',
  'Data Measurement',
  'Condition Configuration',
  'Details and Notifications'
];

export default function Setup({ goToTypeSelection }) {
  const [tab, setTab] = useState(TAB_METRIC_AND_FILTERING);
  const { alertId } = useParams();
  const isDuplicatingAlert = !!useRouteMatch('/alerts/duplicate');
  const isEditingAlert = !!useRouteMatch('/alerts/new/edit/:alertId');
  const { handleFillAlertForm } = useFillAlertForm();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  const { dispatch } = useAlertForm();

  const handleContinue = useCallback(() => {
    setTab((previousTab) => previousTab + 1);
  }, []);

  const handleExit = useCallback(() => {
    const userConfirmedFormLeaving = window.confirm('Do you really want to leave?');

    if (userConfirmedFormLeaving) {
      dispatch({ type: 'CLEAN_UP' });
      goToTypeSelection();
    }
  }, [dispatch, goToTypeSelection]);

  const handleCreateAlert = useCallback(() => {
    // TODO
  }, []);

  const handleSkip = useCallback(() => {
    setTab((previousTab) => previousTab + 1);
  }, []);

  const FormBody = useMemo(() => {
    switch (tab) {
      case TAB_METRIC_AND_FILTERING:
        return Metric;

      case TAB_DATA_MEASUREMENT:
        return Data;

      case TAB_CONDITION_CONFIGURATION:
        return Condition;

      case TAB_DETAILS_AND_NOTIFICATIONS:
        return Details;

      default:
        return Metric;
    }
  }, [tab]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
  }, []);

  const goToMetricTab = useCallback(() => {
    setTab(TAB_METRIC_AND_FILTERING);
  }, []);

  const goToDataMeasurementTab = useCallback(() => {
    setTab(TAB_DATA_MEASUREMENT);
  }, []);

  const goToConditionTab = useCallback(() => {
    setTab(TAB_CONDITION_CONFIGURATION);
  }, []);

  const goToDetailsAndNotificationsTab = useCallback(() => {
    setTab(TAB_DETAILS_AND_NOTIFICATIONS);
  }, []);

  useEffect(() => {
    if (isDuplicatingAlert || isEditingAlert) {
      handleFillAlertForm(alertId);
    }
  }, [alertId, handleFillAlertForm, isDuplicatingAlert, isEditingAlert]);

  return (
    <Form onSubmit={handleSubmit} id="new-alert-create-form">
      <Layout>
        <Layout.Section annotated>
          <Progress>
            <Progress.Step
              completed={tab > TAB_METRIC_AND_FILTERING}
              selected={tab === TAB_METRIC_AND_FILTERING}
              onClick={goToMetricTab}
            >
              {tabsLabels[0]}
            </Progress.Step>
            <Progress.Step
              completed={tab > TAB_DATA_MEASUREMENT}
              selected={tab === TAB_DATA_MEASUREMENT}
              onClick={goToDataMeasurementTab}
            >
              {tabsLabels[1]}
            </Progress.Step>
            <Progress.Step
              completed={tab > TAB_CONDITION_CONFIGURATION}
              selected={tab === TAB_CONDITION_CONFIGURATION}
              onClick={goToConditionTab}
            >
              {tabsLabels[2]}
            </Progress.Step>
            <Progress.Step
              completed={tab > TAB_DETAILS_AND_NOTIFICATIONS}
              selected={tab === TAB_DETAILS_AND_NOTIFICATIONS}
              onClick={goToDetailsAndNotificationsTab}
            >
              {tabsLabels[3]}
            </Progress.Step>
          </Progress>
        </Layout.Section>
        <Layout.Section>
          <Box mb={tokens.spacing_900} maxWidth="786px">
            <Stack space={tokens.spacing_500}>
              <Panel>
                <Panel.Section>
                  <FormBody />
                </Panel.Section>
              </Panel>

              {tab === TAB_DETAILS_AND_NOTIFICATIONS && (
                <Panel>
                  <Panel.Section>
                    <Notifications />
                  </Panel.Section>
                </Panel>
              )}
              <AlertSummary />
            </Stack>
          </Box>
        </Layout.Section>
      </Layout>
      <Footer label={tabsLabels[tab - 1]}>
        {isEditingAlert && tab !== TAB_DETAILS_AND_NOTIFICATIONS && (
          <Button variant="tertiary" mr="8px" onClick={handleSkip}>
            <Text>Skip</Text>
          </Button>
        )}
        {(tab !== TAB_DETAILS_AND_NOTIFICATIONS || !isEditingAlert) && (
          <Button variant="secondary" mr="8px" onClick={handleExit}>
            <Text>{isEditingAlert ? 'Save and Exit' : 'Exit'}</Text>
          </Button>
        )}
        {tab === TAB_DETAILS_AND_NOTIFICATIONS ? (
          <Button variant="primary" onClick={handleCreateAlert}>
            <Text>{isEditingAlert ? 'Save and Exit' : 'Create Alert'}</Text>
          </Button>
        ) : (
          <Button variant="primary" type="submit" onClick={handleContinue}>
            <Text>{isEditingAlert ? 'Save and Continue' : 'Continue'}</Text>
          </Button>
        )}
      </Footer>
    </Form>
  );
}
